import { CSSProperties, ReactElement } from 'react';
import { useActiveUserContext, useAppConfigContext, useAuthServiceContext, useSearchApiContext } from '@vivli/core/infrastructure/context';
import { ContextInjectorComponent } from '@vivli/shared/components';
import {
    SearchBannerComponent,
    SearchFiltersComponent,
    SearchResultsBarComponent,
    SearchTabsComponent
} from '@vivli/features/search/components';
import { SearchTabEnum } from '@vivli/features/search/infrastructure/enum';
import {
    AmrSearchOptionsContextWrapper,
    CtSearchOptionsContextWrapper,
    PicoSearchOptionsContextWrapper
} from '@vivli/features/search/infrastructure/wrappers';
import { useDisplayBannerHook } from '@vivli/features/search/infrastructure/hook';
import { AssetsConstant } from '@vivli/shared/infrastructure/constants';
import { LoginPromptComponent } from '@vivli/features/authentication/components';

const contextWrappers: ReactElement[] = [
    <AmrSearchOptionsContextWrapper />,
    <CtSearchOptionsContextWrapper />,
    <PicoSearchOptionsContextWrapper />
];

export const SearchFeature = () => {
    const config = useAppConfigContext();

    const { user: activeUser } = useActiveUserContext();
    const authService = useAuthServiceContext();
    const { toggleIsShowingResults, searchResultCount, activeTab } = useSearchApiContext();
    const { shouldDisplayBanner } = useDisplayBannerHook();

    const containerStyle: CSSProperties = {
        overflow: 'hidden',
        backgroundImage: `url(${AssetsConstant.VIVLI_BACKGROUND})`,
        display: 'grid',
        position: 'relative',
        gridTemplateRows: shouldDisplayBanner() ? 'auto minmax(200px, 1fr) 70px' : 'minmax(200px, 1fr) 70px',
        height: '100%'
    };

    const showLoginPrompt = !activeUser && config?.loginRequiredForSearch;

    const searchContainerStyle: CSSProperties = {
        display: 'grid',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        padding: '0 80px',
        height: activeTab !== SearchTabEnum.SecondTab ? '100%' : 'initial',
        gridTemplateRows: '90px 1fr',
        gridTemplateColumns: '100%',
        transitionProperty: 'all',
        transitionDuration: '1s',
        transitionTimingFunction: 'cubic-bezier(0, 1, 0.5, 1)'
    };

    if (showLoginPrompt) {
        return <LoginPromptComponent login={authService.login} />;
    }

    return (
        <ContextInjectorComponent contextWrappers={contextWrappers}>
            <div style={containerStyle} id="search-feature-container">
                <SearchBannerComponent />

                <div style={searchContainerStyle}>
                    <SearchTabsComponent />
                    <SearchFiltersComponent />
                </div>

                <SearchResultsBarComponent count={searchResultCount} onClick={toggleIsShowingResults} />
            </div>
        </ContextInjectorComponent>
    );
};
